import React, { useState, useEffect, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { toast } from 'sonner';
import { 
  Download, Upload, Plus, Trash2, Eye, ToggleLeft, ToggleRight, 
  FileSpreadsheet, Calendar, ArrowLeft, ChevronDown, ChevronUp, RefreshCw
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';

const API = '';

const DynamicQuestionnaire = () => {
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  
  const [questionnaires, setQuestionnaires] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [showResponsesModal, setShowResponsesModal] = useState(false);
  const [selectedQuestionnaire, setSelectedQuestionnaire] = useState(null);
  const [responses, setResponses] = useState([]);
  const [expandedId, setExpandedId] = useState(null);
  
  // Upload form state
  const [uploadForm, setUploadForm] = useState({
    file: null,
    title: '',
    description: '',
    start_date: '',
    end_date: ''
  });
  
  // Filters for responses
  const [responseFilters, setResponseFilters] = useState({
    questionnaire_id: '',
    date_from: '',
    date_to: ''
  });

  useEffect(() => {
    fetchQuestionnaires();
  }, []);

  const fetchQuestionnaires = async (isManualRefresh = false) => {
    try {
      if (isManualRefresh) {
        setRefreshing(true);
      } else {
        setLoading(true);
      }
      const res = await axios.get(`${API}/api/admin/questionnaires`);
      setQuestionnaires(res.data.questionnaires || []);
      if (isManualRefresh) {
        toast.success('Data refreshed');
      }
    } catch (error) {
      toast.error('Failed to fetch questionnaires');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const handleRefresh = () => {
    fetchQuestionnaires(true);
  };

  const downloadTemplate = async () => {
    try {
      const res = await axios.get(`${API}/api/admin/questionnaires/template`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'questionnaire_template.xlsx');
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Template downloaded');
    } catch (error) {
      toast.error('Failed to download template');
    }
  };

  const handleUpload = async () => {
    if (!uploadForm.file || !uploadForm.title || !uploadForm.start_date || !uploadForm.end_date) {
      toast.error('Please fill all required fields');
      return;
    }
    
    try {
      const formData = new FormData();
      formData.append('file', uploadForm.file);
      formData.append('title', uploadForm.title);
      formData.append('description', uploadForm.description);
      formData.append('start_date', uploadForm.start_date);
      formData.append('end_date', uploadForm.end_date);
      
      await axios.post(`${API}/api/admin/questionnaires/upload`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      toast.success('Questionnaire uploaded successfully');
      setShowUploadModal(false);
      setUploadForm({ file: null, title: '', description: '', start_date: '', end_date: '' });
      fetchQuestionnaires();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Upload failed');
    }
  };

  const toggleQuestionnaire = async (id) => {
    try {
      const res = await axios.put(`${API}/api/admin/questionnaires/${id}/toggle`);
      toast.success(res.data.message);
      fetchQuestionnaires();
    } catch (error) {
      toast.error('Failed to toggle questionnaire');
    }
  };

  const deleteQuestionnaire = async (id) => {
    if (!window.confirm('Are you sure you want to delete this questionnaire?')) return;
    try {
      await axios.delete(`${API}/api/admin/questionnaires/${id}`);
      toast.success('Questionnaire deleted');
      fetchQuestionnaires();
    } catch (error) {
      toast.error('Failed to delete questionnaire');
    }
  };

  const viewDetails = async (id) => {
    try {
      const res = await axios.get(`${API}/api/admin/questionnaires/${id}`);
      setSelectedQuestionnaire(res.data);
      setExpandedId(expandedId === id ? null : id);
    } catch (error) {
      toast.error('Failed to fetch details');
    }
  };

  const fetchResponses = async () => {
    try {
      const params = new URLSearchParams();
      if (responseFilters.questionnaire_id) params.append('questionnaire_id', responseFilters.questionnaire_id);
      if (responseFilters.date_from) params.append('date_from', responseFilters.date_from);
      if (responseFilters.date_to) params.append('date_to', responseFilters.date_to);
      
      const res = await axios.get(`${API}/api/admin/questionnaires/responses/list?${params}`);
      setResponses(res.data.responses || []);
      setShowResponsesModal(true);
    } catch (error) {
      toast.error('Failed to fetch responses');
    }
  };

  const downloadResponses = async () => {
    try {
      const params = new URLSearchParams();
      if (responseFilters.questionnaire_id) params.append('questionnaire_id', responseFilters.questionnaire_id);
      if (responseFilters.date_from) params.append('date_from', responseFilters.date_from);
      if (responseFilters.date_to) params.append('date_to', responseFilters.date_to);
      
      const res = await axios.get(`${API}/api/admin/questionnaires/responses/download?${params}`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `questionnaire_responses.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Responses downloaded');
    } catch (error) {
      toast.error('Failed to download responses');
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 p-4">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-4">
          <Button variant="ghost" onClick={() => navigate('/admin/dashboard')} data-testid="back-btn">
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <h1 className="text-2xl font-bold text-slate-800">Dynamic Questionnaires</h1>
        </div>
        <div className="flex gap-2">
          <Button 
            variant="outline" 
            onClick={handleRefresh}
            disabled={refreshing || loading}
            data-testid="refresh-questionnaire-btn"
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} /> 
            {refreshing ? 'Refreshing...' : 'Refresh'}
          </Button>
          <Button variant="outline" onClick={downloadTemplate} data-testid="download-template-btn">
            <Download className="w-4 h-4 mr-2" /> Download Template
          </Button>
          <Button 
            onClick={() => setShowUploadModal(true)} 
            data-testid="upload-btn"
            className="bg-red-600 hover:bg-red-700 text-white"
          >
            <Upload className="w-4 h-4 mr-2" /> Upload New
          </Button>
        </div>
      </div>

      {/* Responses Section */}
      <Card className="mb-6">
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <FileSpreadsheet className="w-5 h-5" /> View & Download Responses
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-4 items-end">
            <div>
              <label className="text-sm text-slate-600">Questionnaire</label>
              <select 
                className="w-full border rounded-lg p-2 mt-1"
                value={responseFilters.questionnaire_id}
                onChange={(e) => setResponseFilters({...responseFilters, questionnaire_id: e.target.value})}
              >
                <option value="">All Questionnaires</option>
                {questionnaires.map(q => (
                  <option key={q.questionnaire_id} value={q.questionnaire_id}>{q.title}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-sm text-slate-600">From Date</label>
              <Input type="date" value={responseFilters.date_from} 
                onChange={(e) => setResponseFilters({...responseFilters, date_from: e.target.value})} />
            </div>
            <div>
              <label className="text-sm text-slate-600">To Date</label>
              <Input type="date" value={responseFilters.date_to} 
                onChange={(e) => setResponseFilters({...responseFilters, date_to: e.target.value})} />
            </div>
            <Button variant="outline" onClick={fetchResponses} data-testid="view-responses-btn">
              <Eye className="w-4 h-4 mr-2" /> View Responses
            </Button>
            <Button onClick={downloadResponses} data-testid="download-responses-btn">
              <Download className="w-4 h-4 mr-2" /> Download Excel
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Questionnaires List */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">All Questionnaires ({questionnaires.length})</CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-center py-8 text-slate-500">Loading...</div>
          ) : questionnaires.length === 0 ? (
            <div className="text-center py-8 text-slate-500">No questionnaires yet. Upload one to get started.</div>
          ) : (
            <div className="space-y-4">
              {questionnaires.map(q => (
                <div key={q.questionnaire_id} className={`border rounded-lg p-4 ${q.is_active ? 'bg-green-50 border-green-200' : 'bg-slate-50'}`}>
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <h3 className="font-bold text-lg">{q.title}</h3>
                        <span className={`px-2 py-0.5 rounded-full text-xs font-bold ${q.is_active ? 'bg-green-500 text-white' : 'bg-slate-400 text-white'}`}>
                          {q.is_active ? 'ACTIVE' : 'INACTIVE'}
                        </span>
                      </div>
                      {q.description && <p className="text-sm text-slate-600 mt-1">{q.description}</p>}
                      <div className="flex gap-4 mt-2 text-sm text-slate-500">
                        <span className="flex items-center gap-1">
                          <Calendar className="w-4 h-4" /> {q.start_date} to {q.end_date}
                        </span>
                        <span>{q.questions_count} questions</span>
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <Button variant="ghost" size="sm" onClick={() => viewDetails(q.questionnaire_id)} data-testid={`view-${q.questionnaire_id}`}>
                        {expandedId === q.questionnaire_id ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => toggleQuestionnaire(q.questionnaire_id)} data-testid={`toggle-${q.questionnaire_id}`}>
                        {q.is_active ? <ToggleRight className="w-5 h-5 text-green-600" /> : <ToggleLeft className="w-5 h-5 text-slate-400" />}
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => deleteQuestionnaire(q.questionnaire_id)} data-testid={`delete-${q.questionnaire_id}`}>
                        <Trash2 className="w-4 h-4 text-red-500" />
                      </Button>
                    </div>
                  </div>
                  
                  {/* Expanded Details */}
                  {expandedId === q.questionnaire_id && selectedQuestionnaire && (
                    <div className="mt-4 pt-4 border-t">
                      <h4 className="font-semibold mb-2">Questions:</h4>
                      <div className="space-y-2">
                        {selectedQuestionnaire.questions?.map((question, idx) => (
                          <div key={question.question_id} className="bg-white p-3 rounded border">
                            <div className="flex justify-between">
                              <span className="font-medium">Q{idx + 1}: {question.question_text}</span>
                              <span className={`text-xs px-2 py-0.5 rounded ${
                                question.question_type === 'YES_NO' ? 'bg-blue-100 text-blue-700' :
                                question.question_type === 'DROPDOWN' ? 'bg-purple-100 text-purple-700' :
                                'bg-slate-100 text-slate-700'
                              }`}>
                                {question.question_type}
                              </span>
                            </div>
                            {question.options?.length > 0 && (
                              <div className="text-sm text-slate-500 mt-1">
                                Options: {question.options.join(', ')}
                              </div>
                            )}
                            <div className="text-xs text-slate-400 mt-1">
                              {question.is_required ? 'Required' : 'Optional'}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Upload Modal */}
      {showUploadModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-6 max-w-md w-full mx-4">
            <h2 className="text-xl font-bold mb-4">Upload New Questionnaire</h2>
            
            <div className="space-y-4">
              <div>
                <label className="text-sm font-medium">Title *</label>
                <Input 
                  value={uploadForm.title} 
                  onChange={(e) => setUploadForm({...uploadForm, title: e.target.value})}
                  placeholder="e.g., Weekly Park Assessment"
                />
              </div>
              
              <div>
                <label className="text-sm font-medium">Description</label>
                <Input 
                  value={uploadForm.description} 
                  onChange={(e) => setUploadForm({...uploadForm, description: e.target.value})}
                  placeholder="Optional description"
                />
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium">Start Date *</label>
                  <Input 
                    type="date" 
                    value={uploadForm.start_date}
                    onChange={(e) => setUploadForm({...uploadForm, start_date: e.target.value})}
                  />
                </div>
                <div>
                  <label className="text-sm font-medium">End Date *</label>
                  <Input 
                    type="date" 
                    value={uploadForm.end_date}
                    onChange={(e) => setUploadForm({...uploadForm, end_date: e.target.value})}
                  />
                </div>
              </div>
              
              <div>
                <label className="text-sm font-medium">Excel File *</label>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".xlsx,.xls"
                  onChange={(e) => setUploadForm({...uploadForm, file: e.target.files[0]})}
                  className="w-full border rounded-lg p-2 mt-1"
                />
                <p className="text-xs text-slate-500 mt-1">Download template first, fill it, then upload</p>
              </div>
            </div>
            
            <div className="flex gap-2 mt-6">
              <Button variant="outline" className="flex-1" onClick={() => setShowUploadModal(false)}>
                Cancel
              </Button>
              <Button className="flex-1" onClick={handleUpload} data-testid="submit-upload-btn">
                Upload
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Responses Modal */}
      {showResponsesModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-6 max-w-4xl w-full mx-4 max-h-[80vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold">Questionnaire Responses ({responses.length})</h2>
              <Button variant="ghost" onClick={() => setShowResponsesModal(false)}>×</Button>
            </div>
            
            {responses.length === 0 ? (
              <p className="text-center text-slate-500 py-8">No responses found</p>
            ) : (
              <div className="space-y-4">
                {responses.map(resp => (
                  <div key={resp.response_id} className="border rounded-lg p-4">
                    <div className="flex justify-between mb-2">
                      <div>
                        <span className="font-bold">{resp.questionnaire_title}</span>
                        <span className="text-sm text-slate-500 ml-2">{resp.date}</span>
                      </div>
                      <div className="text-sm text-slate-600">
                        {resp.supervisor_name} ({resp.supervisor_id}) | {resp.park_name}
                      </div>
                    </div>
                    <div className="space-y-1">
                      {resp.answers?.map((ans, idx) => (
                        <div key={idx} className="text-sm flex gap-2">
                          <span className="text-slate-500">Q{idx+1}:</span>
                          <span className="font-medium">{ans.answer}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default DynamicQuestionnaire;
